import React, { useState } from 'react';
import {
  CONDITIONS, PART_TIERS, ROAD_LEGALITY, SORT_OPTIONS, COUNTRIES,
  regionsForCountry, BOLT_PATTERNS, WHEEL_DIAMETERS, WHEEL_WIDTHS,
  WHEELS_DEPARTMENT_SLUG,
} from '../../constants/marketplace';

// ─── Inline stroked SVG icons (Lucide / Feather style — never emojis) ───────
const CloseIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Selected state uses WHITE (per design rule), never the blue accent.
function Chip({ label, active, onClick }) {
  return (
    <button type="button" className={`mp-chip${active ? ' is-active' : ''}`} onClick={onClick}>
      {label}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="mp-filter-section">
      <div className="mp-filter-section__title">{title}</div>
      <div className="mp-chip-wrap">{children}</div>
    </div>
  );
}

// Filters that map to getListings query params. sortBy keeps a default; the
// rest are empty strings (toggled off) by default. universalOnly is a boolean
// here and serialised to 'true' only when on, matching the API contract.
const EMPTY = {
  sortBy: 'newest', condition: '', partTier: '', minPrice: '', maxPrice: '',
  country: '', region: '', roadLegality: '', universalOnly: false,
  boltPattern: '', wheelDiameter: '', wheelWidth: '', offsetMin: '', offsetMax: '',
};

// Panel of marketplace filters. Props { value, onApply, onClose }.
// Wheel facets (boltPattern, diameter, width, offset) render only when the
// selected department is the wheels department. onApply receives the merged
// filter object ready to spread into getListings params.
export default function MarketplaceFilterSheet({ value, onApply, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...value });

  const showWheels = (value?.departmentSlug || f.departmentSlug) === WHEELS_DEPARTMENT_SLUG;

  // toggle: tapping an active value clears it (matches the app sheet)
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: prev[k] === v ? '' : v }));
  const setRaw = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const toggleSort = (v) => setF((prev) => ({ ...prev, sortBy: v }));
  const setCountry = (v) =>
    setF((prev) => ({ ...prev, country: prev.country === v ? '' : v, region: '' }));

  const reset = () => setF({ ...EMPTY, sortBy: f.sortBy, departmentSlug: f.departmentSlug });

  const apply = () => {
    onApply(f);
    if (onClose) onClose();
  };

  const stop = (e) => e.stopPropagation();

  return (
    <div className="mp-modal-backdrop" onClick={onClose}>
      <div className="mp-modal" onClick={stop} role="dialog" aria-modal="true">
        <div className="mp-modal__header">
          <span className="mp-modal__title">Filters</span>
          <button type="button" className="mp-modal__icon-btn" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="mp-modal__body">
          {showWheels ? (
            <>
              <div className="mp-filter-group-title">Wheel fitment</div>
              <Section title="Bolt pattern (PCD)">
                {BOLT_PATTERNS.map((b) => (
                  <Chip key={b} label={b} active={f.boltPattern === b} onClick={() => set('boltPattern', b)} />
                ))}
              </Section>
              <Section title="Diameter (in)">
                {WHEEL_DIAMETERS.map((d) => (
                  <Chip key={d} label={`${d}"`} active={f.wheelDiameter === String(d)} onClick={() => set('wheelDiameter', String(d))} />
                ))}
              </Section>
              <Section title="Width (in)">
                {WHEEL_WIDTHS.map((w) => (
                  <Chip key={w} label={`${w}`} active={f.wheelWidth === String(w)} onClick={() => set('wheelWidth', String(w))} />
                ))}
              </Section>
              <div className="mp-filter-section">
                <div className="mp-filter-section__title">Offset / ET (mm)</div>
                <div className="mp-range-row">
                  <input
                    className="mp-range-input" placeholder="Min" inputMode="numeric"
                    value={f.offsetMin} onChange={(e) => setRaw('offsetMin', e.target.value)}
                  />
                  <span className="mp-range-dash">–</span>
                  <input
                    className="mp-range-input" placeholder="Max" inputMode="numeric"
                    value={f.offsetMax} onChange={(e) => setRaw('offsetMax', e.target.value)}
                  />
                </div>
              </div>
              <div className="mp-filter-divider" />
            </>
          ) : null}

          <Section title="Sort by">
            {SORT_OPTIONS.map((o) => (
              <Chip key={o.value} label={o.label} active={f.sortBy === o.value} onClick={() => toggleSort(o.value)} />
            ))}
          </Section>

          <Section title="Condition">
            {CONDITIONS.map((o) => (
              <Chip key={o.value} label={o.label} active={f.condition === o.value} onClick={() => set('condition', o.value)} />
            ))}
          </Section>

          <Section title="Type">
            {PART_TIERS.map((o) => (
              <Chip key={o.value} label={o.label} active={f.partTier === o.value} onClick={() => set('partTier', o.value)} />
            ))}
          </Section>

          <Section title="Road legality">
            {ROAD_LEGALITY.map((o) => (
              <Chip key={o.value} label={o.label} active={f.roadLegality === o.value} onClick={() => set('roadLegality', o.value)} />
            ))}
          </Section>

          <div className="mp-filter-section">
            <div className="mp-toggle-row">
              <span className="mp-toggle-row__label">Universal parts only</span>
              <button
                type="button"
                className={`mp-switch${f.universalOnly ? ' is-on' : ''}`}
                onClick={() => setRaw('universalOnly', !f.universalOnly)}
                aria-pressed={f.universalOnly}
                aria-label="Universal parts only"
              />
            </div>
          </div>

          <div className="mp-filter-section">
            <div className="mp-filter-section__title">Price ({f.country === 'NZ' ? 'NZD' : 'AUD'})</div>
            <div className="mp-range-row">
              <input
                className="mp-range-input" placeholder="Min" inputMode="numeric"
                value={f.minPrice} onChange={(e) => setRaw('minPrice', e.target.value)}
              />
              <span className="mp-range-dash">–</span>
              <input
                className="mp-range-input" placeholder="Max" inputMode="numeric"
                value={f.maxPrice} onChange={(e) => setRaw('maxPrice', e.target.value)}
              />
            </div>
          </div>

          <Section title="Country">
            {COUNTRIES.map((o) => (
              <Chip key={o.value} label={o.label} active={f.country === o.value} onClick={() => setCountry(o.value)} />
            ))}
          </Section>

          <Section title={(f.country || 'AU') === 'NZ' ? 'Region' : 'State / Territory'}>
            {regionsForCountry(f.country || 'AU').map((r) => (
              <Chip key={r} label={r} active={f.region === r} onClick={() => set('region', r)} />
            ))}
          </Section>
        </div>

        <div className="mp-modal__footer">
          <button type="button" className="mp-filter-reset" onClick={reset}>Reset</button>
          <button type="button" className="mp-filter-apply" onClick={apply}>Apply filters</button>
        </div>
      </div>
    </div>
  );
}
